(function () {
  'use strict';
  const eu = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '));
  const destinations = {
    dk: { host: 'copenhagenhomedesign.dk', language: 'da', label: 'Danmark · Dansk' },
    de: { host: 'copenhagenhomedesign.de', language: 'de', label: 'Deutschland · Deutsch' },
    se: { host: 'copenhagenhomedesign.se', language: 'sv', label: 'Sverige · Svenska' },
    com: { host: 'copenhagenhomedesign.com', language: 'en', label: 'European Union · English' }
  };
  const normalizeHost = host => String(host).toLowerCase().replace(/^www\./, '');
  function destination(country) {
    const code = String(country || '').toUpperCase();
    if (!eu.has(code)) return null;
    const group = { DK: 'dk', DE: 'de', SE: 'se' }[code] || 'com';
    return { ...destinations[group], group, country: code };
  }
  function recommendation(country, host, config) {
    const target = destination(country);
    const current = normalizeHost(host);
    if (!target || !Object.values(destinations).some(value => value.host === current)) return null;
    if (target.host === current || config.ready?.[target.group] !== true) return null;
    return config.countries?.includes(target.country) ? target : null;
  }
  async function confirmCartSwitch(root, copy, fetchCart, confirm, alert) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetchCart(String(root || '/').replace(/\/?$/, '/') + 'cart.js', {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error('Invalid cart');
      return cart.item_count === 0 || confirm(copy.warning);
    } catch (_) {
      alert(copy.error);
      return false;
    } finally { clearTimeout(timeout); }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { destination, recommendation, confirmCartSwitch };
  if (typeof document === 'undefined') return;
  const storageKey = 'chd-market-choice-v1';
  function remember() {
    try { localStorage.setItem(storageKey, String(Date.now())); } catch (_) {}
  }
  function dismissed() {
    try {
      const timestamp = Number(localStorage.getItem(storageKey));
      const age = Date.now() - timestamp;
      return timestamp > 0 && age >= 0 && age < 30 * 86400000;
    } catch (_) { return false; }
  }
  const arrival = new URL(location.href);
  if (arrival.searchParams.get('chd_market_choice') === '1') {
    remember();
    arrival.searchParams.delete('chd_market_choice');
    history.replaceState(history.state, '', arrival.pathname + arrival.search + arrival.hash);
  }
  const pending = new WeakSet();
  const approved = new WeakSet();
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-chd-store-link]');
    if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href);
    if (normalizeHost(url.hostname) === normalizeHost(location.hostname)) return;
    event.preventDefault();
    if (pending.has(link)) return;
    let copy;
    try { copy = JSON.parse(document.getElementById('chd-market-cart-guard').textContent); } catch (_) { return; }
    pending.add(link);
    confirmCartSwitch(copy.root, copy, window.fetch.bind(window), window.confirm.bind(window), window.alert.bind(window))
      .then(allowed => {
        if (!allowed) return;
        remember();
        url.searchParams.set('chd_market_choice', '1');
        location.assign(url.href);
      })
      .finally(() => pending.delete(link));
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-chd-localization], #chd-market-form')) return;
    const country = form.elements.country_code?.value;
    const target = destination(country);
    if (target && normalizeHost(location.hostname) !== target.host && !approved.has(form)) {
      event.preventDefault();
      if (pending.has(form)) return;
      const guard = document.getElementById('chd-market-cart-guard');
      if (!guard) return;
      let copy;
      try { copy = JSON.parse(guard.textContent); } catch (_) { return; }
      pending.add(form);
      confirmCartSwitch(copy.root, copy, window.fetch.bind(window), window.confirm.bind(window), window.alert.bind(window))
        .then(allowed => {
          if (!allowed || !form.isConnected || form.elements.country_code.value !== country) return;
          approved.add(form);
          try { form.requestSubmit(event.submitter || undefined); } finally { approved.delete(form); }
        })
        .finally(() => pending.delete(form));
      return;
    }
    remember();
    if (form.id === 'chd-footer-country') {
      const target = destination(form.elements.country_code.value);
      if (target) {
        let language = form.elements.language_code;
        if (!language) {
          language = document.createElement('input');
          language.type = 'hidden';
          language.name = 'language_code';
          form.append(language);
        }
        language.value = target.language;
      }
    }
    let returnTo = form.elements.return_to;
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.append(returnTo);
    }
    const returnUrl = new URL(returnTo.value || location.pathname + location.search, location.origin);
    returnUrl.searchParams.set('chd_market_choice', '1');
    returnTo.value = returnUrl.pathname + returnUrl.search + location.hash;
  });
  async function init() {
    const dialog = document.getElementById('chd-market-dialog');
    const element = document.getElementById('chd-market-config');
    if (!dialog || !element || !dialog.showModal || dismissed() || window.Shopify?.designMode) return;
    let config;
    try { config = JSON.parse(element.textContent); } catch (_) { return; }
    if (!Object.values(config.ready || {}).some(Boolean)) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    let target;
    try {
      const root = String(config.root || '/').replace(/\/?$/, '/');
      const response = await fetch(root + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false', { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      target = recommendation(data.detected_values?.country?.handle, location.hostname, config);
    } catch (_) { return; } finally { clearTimeout(timeout); }
    if (!target || dismissed()) return;
    const form = dialog.querySelector('form');
    form.elements.country_code.value = target.country;
    form.elements.language_code.value = target.language;
    dialog.querySelector('[data-market-destination]').textContent = target.label + ' · ' + target.host;
    const dismiss = () => { remember(); dialog.close(); };
    dialog.querySelectorAll('[data-market-dismiss]').forEach(button => button.addEventListener('click', dismiss));
    dialog.addEventListener('cancel', remember);
    const start = Date.now();
    const show = () => {
      if (dismissed() || Date.now() - start > 60000) return;
      const otherModalOpen = Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some(element => {
        if (element === dialog || element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
        const style = getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
      });
      if (document.hidden || otherModalOpen || /INPUT|SELECT|TEXTAREA/.test(document.activeElement?.tagName || '')) {
        setTimeout(show, 2000);
        return;
      }
      dialog.showModal();
    };
    setTimeout(show, 1800);
  }
  init();
})();
